import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import './CompetitiveData.css';

const CompetitiveData = () => {
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('usagePercent');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef(null);

  // Save scroll position before navigating away
  const handlePokemonClick = (pokedexId) => {
    const currentScroll = gridRef.current?.scrollTop || window.scrollY;
    navigate(`/pokemon/${pokedexId}`, { 
      state: { 
        from: 'competitive',
        scrollPosition: currentScroll 
      } 
    });
  };

  // Restore scroll position when component mounts or when returning from a Pokemon detail page
  useEffect(() => {
    if (location.state?.scrollPosition && !loading) {
      const scrollPosition = location.state.scrollPosition;
      if (gridRef.current) {
        gridRef.current.scrollTop = scrollPosition;
      } else {
        window.scrollTo(0, scrollPosition);
      }
    }
  }, [location.state?.scrollPosition, loading]);

  const fetchPokemon = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/competitive/pokemon?limit=100&sort=${sortBy}&order=${sortOrder}&search=${searchTerm}&type=${typeFilter}`);
      if (!response.ok) {
        throw new Error('Failed to fetch Pokemon data');
      }
      const data = await response.json();
      setPokemon(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setPokemon([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, searchTerm, typeFilter]);

  useEffect(() => {
    fetchPokemon();
  }, [fetchPokemon]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTypeFilter = (e) => {
    setTypeFilter(e.target.value);
  };

  const getPokemonSprite = (p) => {
    return p.sprites?.other?.['official-artwork']?.front_default || 
           p.sprites?.front_default || 
           `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${p.pokedexId}.png`;
  };

  // Filter out "Other" from abilities, items, and moves
  const filterOutOther = (items) => {
    return items?.filter(item => item.name.toLowerCase() !== 'other') || [];
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="competitive-data">
      <div className="competitive-data__header">
        <h1>Commonly Used Pokemon</h1>
      </div>

      <div className="competitive-data__grid" ref={gridRef}>
        {pokemon.map((p) => (
          <div key={p.pokedexId} className="competitive-data__card">
            <div className="competitive-data__card-header">
              <div className="competitive-data__card-info">
                <div 
                  onClick={() => handlePokemonClick(p.pokedexId)}
                  className="competitive-data__pokemon-link"
                  style={{ cursor: 'pointer' }}
                >
                  <h3 className="competitive-data__name">{p.name}</h3>
                </div>
                <div className="type-badges">
                  {p.types?.map((type) => (
                    <span
                      key={`${p.pokedexId}-${type}`}
                      className={`type-badge type-badge--${type.toLowerCase()}`}
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              <div className="competitive-data__sprite-container">
                <img
                  src={getPokemonSprite(p)}
                  alt={p.name}
                  className="competitive-data__sprite"
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    // First try the regular sprite
                    e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${p.pokedexId}.png`;
                    // If that fails too, use a placeholder
                    e.target.onerror = () => {
                      e.target.src = '/placeholder-pokemon.png';
                      e.target.onerror = null;
                    };
                  }}
                />
              </div>
            </div>

            <div className="competitive-data__sections">
              <div className="competitive-data__section">
                <h4>Abilities</h4>
                <ul className="competitive-data__list">
                  {filterOutOther(p.abilities)?.map((ability) => (
                    <li key={ability.name}>
                      <Link 
                        to={`/abilities/${ability.name.replace(/\s+/g, '-')}`}
                        className="competitive-data__link"
                        state={{ from: 'competitive', scrollPosition: window.scrollY }}
                      >
                        {ability.name}
                      </Link>
                      <span></span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="competitive-data__section">
                <h4>Common Items</h4>
                <ul className="competitive-data__list">
                  {filterOutOther(p.items)?.map((item) => (
                    <li key={item.name}>
                      <Link 
                        to={`/items/${item.name.replace(/\s+/g, '-')}`}
                        className="competitive-data__link"
                        state={{ from: 'competitive', scrollPosition: window.scrollY }}
                      >
                        {item.name}
                      </Link>
                      <span></span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="competitive-data__section">
                <h4>Common Moves</h4>
                <ul className="competitive-data__list">
                  {filterOutOther(p.moves)?.map((move) => (
                    <li key={move.name}>
                      <Link 
                        to={`/moves/${move.name.replace(/\s+/g, '-')}`}
                        className="competitive-data__link"
                        state={{ from: 'competitive', scrollPosition: window.scrollY }}
                      >
                        {move.name}
                      </Link>
                      <span></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompetitiveData;